import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

/**
 * Handles the /panel/auth/callback route.
 * Emergent redirects here with #session_id=XYZ in the URL fragment.
 * We exchange it server-side for a SyncLine JWT, store as loomi_token, then send to /panel.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function PanelAuthCallback() {
  const nav = useNavigate();
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError("Missing Google session. Please try again.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google", { session_id: sessionId });
        localStorage.setItem("loomi_token", data.access_token);
        // Clear the fragment so it isn't reused
        window.history.replaceState(null, "", "/panel");
        toast.success(`Welcome, ${data.user?.name || "panelist"}!`);
        // eslint-disable-next-line
        window.location.href = "/panel";
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [nav]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" data-testid="panel-auth-callback">
      <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
        <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-emerald-500 to-[#D4A94A] flex items-center justify-center mb-4">
          <Sparkles className="w-5 h-5 text-black" />
        </div>
        {error ? (
          <>
            <h2 className="font-display font-bold text-xl mb-2">Sign-in failed</h2>
            <p className="text-sm text-mute mb-5" data-testid="panel-auth-callback-error">{error}</p>
            <button onClick={() => (window.location.href = "/panel/login")} className="bg-indigo-500 hover:bg-indigo-400 rounded-md px-4 py-2 text-sm font-medium">
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <h2 className="font-display font-bold text-xl mb-2">Signing you in…</h2>
            <p className="text-sm text-mute">One moment while we finish setting up your panelist account.</p>
            <div className="mt-4 h-1 w-full bg-white/5 rounded overflow-hidden">
              <div className="h-full w-1/3 bg-indigo-500 animate-[shimmer_1.2s_ease-in-out_infinite]" />
            </div>
            <style>{`@keyframes shimmer { 0%{transform:translateX(-100%);} 100%{transform:translateX(300%);} }`}</style>
          </>
        )}
      </div>
    </div>
  );
}
